<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-full"
    title="フロー変更"
  >
    <template v-slot:body="{ cancel }">
      <v-form novalidate @submit.stop.prevent="submit">
        <!-- BODY -->
        <v-card-text class="pa-0">
          <div class="modal-body">
            <v-container fluid>
              <v-row>
                <v-col class="d-flex flex-row justify-end">
                  <v-btn color="primary" @click="registerFlow"
                    >フロー設定</v-btn
                  >
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <app-draggable-table
                    v-model="flow.items"
                    primary-key="_index"
                    :fields="flow.fields"
                    :items="flow.items"
                    :hover="true"
                    :fixed="true"
                    density="compact"
                    @click:row="modifyFlow"
                    :tr-class="rowClass"
                  >
                    <template #head(destWnumVpnCode)>
                      接続先VNコネクト名(W番号)/ <br />
                      接続先VPN(VPNコード)
                    </template>
                    <template #cell(delete)="{ item }">
                      <v-btn
                        density="comfortable"
                        variant="flat"
                        color="primary"
                        @click.stop="deleteFlow(item)"
                        >削除
                      </v-btn>
                    </template>
                  </app-draggable-table>
                </v-col>
              </v-row>
            </v-container>
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <!-- FOOTER -->
        <v-card-actions>
          <v-list-item class="w-100 pa-1">
            <template v-slot:append>
              <v-btn type="submit" color="primary" variant="flat">変更</v-btn>
              <v-btn @click="cancel" variant="outlined" color="primary"
                >キャンセル</v-btn
              >
            </template>
          </v-list-item>
        </v-card-actions>
      </v-form>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import { VueFinalModal } from "vue-final-modal";
import type { FlowEntity } from "@/modals/site/SiteReference.vue";
import cloneDeep from "lodash/cloneDeep";
import SiteFlowEdit from "@/modals/site/SiteFlowEdit.vue";
import type { SiteListEntity } from "@/views/nwSetting/site/SiteList.vue";
import AppDraggableTable from "@/components/AppDraggableTable.vue";
import type { AppDraggableTableHeader } from "@/components/AppDraggableTable.vue";
import type { TableItem } from "@/components/AppTable.vue";

export default defineComponent({
  name: "SiteFlowListModify",
  components: {
    AppDraggableTable,
  },
  props: {
    /** サイト情報 */
    siteData: {
      type: Object as PropType<SiteListEntity>,
      required: true,
    },
    /** フロー情報一覧 */
    flowData: {
      type: Array as PropType<FlowEntity[]>,
      required: true,
    },
    /** フロー情報排他制御キー */
    flowKeyId: {
      type: String as PropType<string>,
      required: true,
    },
  },
  data() {
    const flowClone: FlowEntity[] | undefined = cloneDeep(this.flowData);
    // 主キー相当の項目が無いため、こちらでインデックスを作成
    flowClone?.numbering();
    return {
      flow: {
        fields: [
          { key: "_handle", title: "↑↓", width: "100px", tdClass: "handle" },
          {
            key: "destWnumVpnCode",
            title: "",
            width: "240px",
            formatter: (value: string, key: null, item: FlowEntity) => {
              if (item.wnumber) {
                return `${item.vnConnectName}(${item.wnumber})`;
              } else {
                return `L3VPN(${item.vpnCode})`;
              }
            },
          },
          {
            key: "srcAddress",
            title: "送信元アドレス",
            width: "140px",
            formatter: (value: string) => this.$filter.emptyTo(value, "any"),
          },
          {
            key: "dstAddress",
            title: "宛先アドレス",
            width: "140px",
            formatter: (value: string) => this.$filter.emptyTo(value, "any"),
          },
          {
            key: "description",
            title: "備考",
            tdClass: "text-pre-wrap",
            formatter: (value: string) => this.$filter.emptyTo(value, "-"),
          },
          {
            key: "flowStatus",
            title: "有効/無効",
            width: "100px",
            formatter: (value: string) =>
              this.$filter.enumTo(value, "flowStatusItems"),
          },
          {
            key: "ifStatus",
            title: "IF状態",
            width: "100px",
            formatter: (value: string) =>
              this.$filter.enumTo(value, "cloudVn_ifStatus"),
          },
          { key: "delete", title: "", width: "80px" },
        ] as AppDraggableTableHeader<TableItem>[],
        items: flowClone ?? [],
      },
    };
  },
  methods: {
    /** フローを追加 */
    async registerFlow() {
      const addFlow: FlowEntity = await this.$modal.show(SiteFlowEdit, {
        enumber: this.siteData.enumber,
        vlanIdType: this.siteData.vlanIdType,
        vlanId: this.siteData.vlanId,
      });
      this.flow.items.splice(
        this.flow.items.length,
        0,
        this.flow.items.nextNumber({
          ...addFlow,
        })
      );
      this.flow.items.removeNumbering();
    },

    /** フロー情報変更 */
    async modifyFlow(flowDetail: FlowEntity) {
      const updatedItem: FlowEntity = await this.$modal.show(SiteFlowEdit, {
        flow: flowDetail,
        enumber: this.siteData.enumber,
        vlanIdType: this.siteData.vlanIdType,
        vlanId: this.siteData.vlanId,
      });
      const index = this.flow.items.indexOf(flowDetail);
      this.flow.items[index] = updatedItem;
    },

    /** フロー削除 */
    async deleteFlow(flowDetail: FlowEntity) {
      const index = this.flow.items.indexOf(flowDetail);
      this.flow.items.splice(index, 1);
    },

    /** フロー変更 */
    async submit() {
      await this.$confirm(`フロー情報を変更します。よろしいですか？`);
      await this.$api.virtualNetwork.updateFlowSettings({
        enumber: this.siteData.enumber,
        vlanIdType: this.siteData.vlanIdType,
        vlanId:
          this.siteData.vlanIdType === "UNTAG" ||
          this.siteData.vlanIdType === "OTHER"
            ? null
            : this.siteData.vlanId,
        flowModifyList: this.flow.items.map((e) => ({
          flowSeq: e.flowSeq || null,
          wnumber: e.wnumber || null,
          vpnCode: e.vpnCode || null,
          srcAddress: e.srcAddress || null,
          dstAddress: e.dstAddress || null,
          description: e.description || null,
        })),
        flowKeyId: this.flowKeyId,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },

    /**
     * trにクラスを追加
     * 共通クラス：
     * draggable: ドラッグ可能
     * clickable: 行クリック可能
     * gray-out : 行灰色
     */
    rowClass() {
      // draggable可、click可、白色
      return ["draggable", "clickable"];
    },
  },
});
</script>
